package top.wcpe.allincore.ui.runtime.animation

import top.wcpe.allincore.ui.runtime.internal.concurrent.SyncLock
import top.wcpe.allincore.ui.runtime.internal.concurrent.newConcurrentList
import top.wcpe.allincore.ui.runtime.internal.concurrent.withLock
import top.wcpe.allincore.ui.runtime.reactive.EffectBody
import kotlin.concurrent.Volatile
import kotlin.jvm.JvmField

/**
 * 动画播放句柄。
 * 提供对单条进行中动画的控制能力：取消、暂停/恢复、跳转、停止复位、完成回调。
 */
class AnimationHandle {

    @Volatile
    private var cancelledValue: Boolean = false

    @Volatile
    private var finishedValue: Boolean = false

    @Volatile
    private var pausedValue: Boolean = false

    /** 完成回调列表（EffectBody 对齐 UIElement.animationCompleteCallbacks）；finish() 时统一触发，pause/cancel 不触发。 */
    private val onCompleteCallbacks: MutableList<EffectBody> = newConcurrentList()

    /** onComplete/finish 的互斥锁（原 @Synchronized 的平台中立替代）。 */
    private val completionLock = SyncLock()

    /** 由 AnimationScheduler 在 addActive 时注入。 */
    @JvmField
    internal var scheduler: AnimationScheduler? = null

    /** 由 AnimationScheduler 在 addActive 时注入。 */
    @JvmField
    internal var active: AnimationScheduler.ActiveAnimation? = null

    fun cancel() {
        cancelledValue = true
    }
    fun isCancelled(): Boolean = cancelledValue
    fun isFinished(): Boolean = finishedValue
    fun isPaused(): Boolean = pausedValue

    /** 冻结当前进度。已取消/已完成/已暂停时无效。 */
    fun pause() {
        scheduler?.pauseAnimation(this)
    }

    /** 从暂停处继续。未暂停时无效。 */
    fun resume() {
        scheduler?.resumeAnimation(this)
    }

    /** 跳转到归一化进度 [0,1]。已取消/已完成时无效。暂停状态下 seek 不会自动恢复播放。 */
    fun seek(t01: Double) {
        scheduler?.seekAnimation(this, t01)
    }

    /** 复位被影响的属性到起始值并取消该动画。不触发 onComplete。 */
    fun stop() {
        scheduler?.stopAnimation(this) ?: run { cancelledValue = true }
    }

    /**
     * 注册完成回调。若动画已完成则立刻触发。
     * 多次注册按注册顺序在 finish() 时同步调用。
     */
    fun onComplete(callback: EffectBody?): AnimationHandle {
        if (callback == null) return this
        completionLock.withLock {
            if (finishedValue) {
                safeRun(callback)
            } else {
                onCompleteCallbacks.add(callback)
            }
        }
        return this
    }

    fun setPaused(value: Boolean) {
        pausedValue = value
    }

    fun finish() {
        completionLock.withLock {
            if (finishedValue) return
            finishedValue = true
            for (r in onCompleteCallbacks) safeRun(r)
            onCompleteCallbacks.clear()
        }
    }

    companion object {
        /** 完成回调失败不应影响其他回调或调度器主循环。 */
        private fun safeRun(r: EffectBody) {
            try {
                r.run()
            } catch (ignored: RuntimeException) {
                // 完成回调失败不应影响其他回调或调度器主循环
            }
        }
    }
}
